import classNames from 'classnames';
import dayjs from 'dayjs';
import React, { FC, memo, ReactNode } from 'react';
import { useGetCurrencySymbol } from 'app/appState';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { CloseContractPreview as CloseContractPreviewData, PreviewInvoiceBreakdown } from 'entities/Contract';
import { Button } from 'shared/ui/Button';
import { WarnTag } from 'shared/ui/WarnTag';
import { getAmountWithCurrency } from 'shared/utils/helpers/getAmountWithCurrency';
import { getClientDateFormat, getClientShortDateFormat } from 'shared/utils/helpers/getDateFormat';

// font sizes are pinned in px: global styles apply font-weight 500 and font-size 14px to every
// element, which also shrinks tailwind's rem-based text-* sizes, so they are set on the leaf nodes

interface EffectRowProps {
  label: string;
  amount?: string;
  theme?: 'refund' | 'charge' | 'muted' | 'neutral';
}

const EffectRow: FC<EffectRowProps> = memo((props) => {
  const { label, amount, theme = 'neutral' } = props;

  return (
    <div className="flex justify-between gap-3 py-[3px]">
      <span className="text-[13px] font-normal text-primaryLight">{label}</span>
      {amount && (
        <span
          className={classNames('whitespace-nowrap text-[13px]', {
            'font-semibold text-success': theme === 'refund',
            'font-semibold text-warn': theme === 'charge',
            'font-semibold': theme === 'neutral',
            'font-normal text-primaryLight': theme === 'muted',
          })}
        >
          {amount}
        </span>
      )}
    </div>
  );
});

interface SectionProps {
  title: string;
  summary?: string;
}

const SectionTitle: FC<SectionProps> = memo(({ title, summary }) => (
  <div className="flex justify-between items-center gap-3 border-t border-primary pt-2.5 mt-2.5 mb-1">
    <span className="text-[12px] font-normal text-primaryLight">{title}</span>
    {summary && <span className="text-[13px] font-semibold whitespace-nowrap">{summary}</span>}
  </div>
));

interface InvoiceRowProps {
  invoice: PreviewInvoiceBreakdown;
  currency: string;
  closeDate: string;
  willIssuePenaltyInvoice: boolean;
}

const InvoiceRow: FC<InvoiceRowProps> = memo(({ invoice, currency, closeDate, willIssuePenaltyInvoice }) => {
  const { t } = useAppTranslation('contracts');

  const periodAmount = invoice.usedAmount + invoice.unusedAmount;

  // the bar only carries information when the close date actually splits the period
  const showPeriodBar = Boolean(invoice.dateFrom && invoice.dateTo && invoice.usedAmount > 0 && invoice.unusedAmount > 0);

  const getPaymentStatus = (): string => {
    if (invoice.paidAmount >= invoice.totalAmount) {
      return t('Paid');
    }

    return invoice.paidAmount > 0 ? t('Partial payment') : t('Unpaid');
  };

  const metaParts = [
    invoice.dateFrom && invoice.dateTo ? `${getClientDateFormat(invoice.dateFrom)} – ${getClientDateFormat(invoice.dateTo)}` : null,
    invoice.isDepositInvoice ? t('Deposit invoice') : null,
    getPaymentStatus(),
  ].filter(Boolean);

  return (
    <div className={classNames({ 'mb-2.5': invoice.isAffected })}>
      <div className="flex justify-between gap-3 py-[3px]">
        <span className="min-w-0">
          <span className="text-[13px] font-medium">#{invoice.documentNumber}</span>{' '}
          <span className="text-[11.5px] font-normal text-primaryLight">{metaParts.join(' · ')}</span>
        </span>
        <span
          className={classNames('text-[13px] whitespace-nowrap', {
            'font-semibold': invoice.isAffected,
            'font-normal text-primaryLight': !invoice.isAffected,
          })}
        >
          {invoice.isAffected ? getAmountWithCurrency(invoice.totalAmount, currency) : t('No changes')}
        </span>
      </div>

      {showPeriodBar && (
        <>
          <div className="flex h-1.5 rounded overflow-hidden mt-1 mb-1" aria-hidden="true">
            <div className="bg-primary" style={{ width: `${(invoice.usedAmount / periodAmount) * 100}%` }} />
            <div className="bg-successLight" style={{ width: `${(invoice.unusedAmount / periodAmount) * 100}%` }} />
          </div>
          <div className="flex justify-between gap-3 mb-1">
            <span className="text-[11.5px] font-normal text-primaryLight">
              {t('Used')} {getClientShortDateFormat(invoice.dateFrom)}–{getClientShortDateFormat(closeDate)}
            </span>
            <span className="text-[11.5px] font-normal text-primaryLight text-right">
              {t('Unused')} {getClientShortDateFormat(dayjs(closeDate).add(1, 'day'))}–{getClientShortDateFormat(invoice.dateTo)}
            </span>
          </div>
        </>
      )}

      {invoice.depositChargeAmount > 0 && (
        <EffectRow
          label={t('Charged from deposit')}
          amount={`−${getAmountWithCurrency(invoice.depositChargeAmount, currency)}`}
          theme="charge"
        />
      )}
      {invoice.balanceChargeAmount > 0 && (
        <EffectRow
          label={t('Charged from user balance')}
          amount={`−${getAmountWithCurrency(invoice.balanceChargeAmount, currency)}`}
          theme="charge"
        />
      )}
      {invoice.penaltyAmount > 0 && (
        <EffectRow
          label={willIssuePenaltyInvoice ? t('Remaining debt to penalty invoice') : t('Remaining debt written off')}
          amount={getAmountWithCurrency(invoice.penaltyAmount, currency)}
          theme={willIssuePenaltyInvoice ? 'charge' : 'neutral'}
        />
      )}
      {invoice.creditNoteAmount > 0 && (
        <EffectRow label={t('Credited by credit note')} amount={getAmountWithCurrency(invoice.creditNoteAmount, currency)} />
      )}
    </div>
  );
});

interface CloseContractPreviewProps {
  preview?: CloseContractPreviewData;
  isFetching: boolean;
  isError: boolean;
  errorMessage?: string;
  onRetry: () => void;
}

export const CloseContractPreview: FC<CloseContractPreviewProps> = memo((props) => {
  const { preview, isFetching, isError, errorMessage, onRetry } = props;

  const { t } = useAppTranslation('contracts');

  // hook must run before the early returns below
  const currency = useGetCurrencySymbol();

  // the status only appears while recalculating - a steady "updated" badge carries no information
  const heading = (isBusy = false): ReactNode => (
    // right padding keeps the status clear of the modal close icon on desktop
    <div className="flex justify-between items-center gap-3 mb-2 desktop:pr-7">
      <span className="text-[11.5px] font-semibold uppercase tracking-wider text-primaryLight">{t('Preview')}</span>
      {isBusy && <span className="text-[11.5px] font-normal text-primaryLight whitespace-nowrap">{t('Recalculating')}</span>}
    </div>
  );

  if (isError) {
    return (
      <div>
        {heading()}
        <WarnTag textSize="small" className="!items-start">
          <span className="text-[13px] font-normal">
            {t('Preview is unavailable, so the outcome of closing is not shown')}
            {errorMessage && <span className="block text-[13px] font-semibold mt-1">{errorMessage}</span>}
          </span>
        </WarnTag>
        <Button theme="clear" onClick={onRetry} className="!px-0 mt-1">
          {t('Try again')}
        </Button>
      </div>
    );
  }

  if (!preview) {
    return (
      <div>
        {heading(true)}
        <div className="animate-pulse flex flex-col gap-2">
          <div className="h-3 w-3/5 rounded bg-secondaryLight" />
          <div className="h-3 w-4/5 rounded bg-secondaryLight" />
          <div className="h-3 w-2/3 rounded bg-secondaryLight" />
          <div className="h-3 w-3/4 rounded bg-secondaryLight" />
          <div className="h-3 w-1/2 rounded bg-secondaryLight" />
        </div>
      </div>
    );
  }

  const affectedInvoices = preview.invoices.filter((invoice) => invoice.isAffected);
  const unchangedInvoices = preview.invoices.filter((invoice) => !invoice.isAffected);

  // few untouched invoices stay visible as one-liners, a long tail collapses into a single note
  const shouldFoldUnchanged = unchangedInvoices.length > 3;

  const hasDepositMovement = Boolean(preview.deposit && (preview.deposit.chargedAmount > 0 || preview.deposit.refundAmount > 0));

  // an unpaid deposit would render a section of zeroes - the invoice meta already says the deposit exists
  const hasDepositActivity = hasDepositMovement || Boolean(preview.deposit && preview.deposit.paidAmount > 0);

  const isAllClear =
    affectedInvoices.length === 0 &&
    preview.refunds.length === 0 &&
    !preview.penaltyInvoice &&
    !hasDepositMovement &&
    preview.totals.refundAmount === 0;

  return (
    <div className={classNames('transition-opacity', { 'opacity-60': isFetching })} aria-busy={isFetching}>
      {heading(isFetching)}

      {isAllClear ? (
        <div className="rounded-xl bg-successLight p-3">
          <span className="text-[13px] font-normal text-success">{t('Closing will not create any financial operations')}</span>
        </div>
      ) : (
        <>
          <SectionTitle title={t('Contract invoices')} summary={String(preview.invoices.length)} />

          {affectedInvoices.map((invoice) => (
            <InvoiceRow
              key={invoice.invoiceId}
              invoice={invoice}
              currency={currency}
              closeDate={preview.closeDate}
              willIssuePenaltyInvoice={Boolean(preview.penaltyInvoice)}
            />
          ))}

          {shouldFoldUnchanged ? (
            <div className="rounded-lg bg-secondaryLight px-3 py-1.5 mt-1">
              <span className="text-[11.5px] font-normal text-primaryLight">
                {t('{{amount}} invoices unchanged', { amount: unchangedInvoices.length })}
              </span>
            </div>
          ) : (
            unchangedInvoices.map((invoice) => (
              <InvoiceRow
                key={invoice.invoiceId}
                invoice={invoice}
                currency={currency}
                closeDate={preview.closeDate}
                willIssuePenaltyInvoice={Boolean(preview.penaltyInvoice)}
              />
            ))
          )}

          {preview.deposit && hasDepositActivity && (
            <>
              <SectionTitle title={t('Deposit')} />
              <EffectRow
                label={preview.deposit.invoiceNumber ? `${t('Deposit paid')} (#${preview.deposit.invoiceNumber})` : t('Deposit paid')}
                amount={getAmountWithCurrency(preview.deposit.paidAmount, currency)}
              />
              {preview.deposit.chargedAmount > 0 && (
                <EffectRow
                  label={t('Charged towards debts')}
                  amount={`−${getAmountWithCurrency(preview.deposit.chargedAmount, currency)}`}
                  theme="charge"
                />
              )}
              <EffectRow
                label={t('To be refunded')}
                amount={`+${getAmountWithCurrency(preview.deposit.refundAmount, currency)}`}
                theme={preview.deposit.refundAmount > 0 ? 'refund' : 'neutral'}
              />
            </>
          )}

          {preview.userBalance.chargedAmount > 0 && (
            <>
              <SectionTitle title={t('User balance')} />
              <EffectRow
                label={t('Charged towards debts')}
                amount={`−${getAmountWithCurrency(preview.userBalance.chargedAmount, currency)}`}
                theme="charge"
              />
            </>
          )}

          <SectionTitle
            title={t('Refunds')}
            summary={preview.refunds.length ? getAmountWithCurrency(preview.totals.refundAmount, currency) : undefined}
          />

          {preview.refunds.length === 0 ? (
            <EffectRow label={t('No refunds will be made')} />
          ) : (
            preview.refunds.map((refund) => (
              <div key={`${refund.paymentId}-${String(refund.isDepositRefund)}`} className="flex justify-between gap-3 py-[3px]">
                <span className="min-w-0">
                  <span className="text-[13px] font-normal">#{refund.documentNumber}</span>
                  {/* the flag means "paid against the deposit invoice", which may also carry rent or insurance */}
                  {refund.isDepositRefund && (
                    <span className="ml-1.5 rounded bg-secondaryLight text-primaryLight text-[10px] font-normal px-1.5">
                      {t('Deposit invoice')}
                    </span>
                  )}
                  <span className="block text-[11.5px] font-normal text-primaryLight">
                    {refund.paymentMethod}
                    {refund.paidAt ? ` · ${getClientDateFormat(refund.paidAt)}` : ''}
                  </span>
                </span>
                <span className="text-[13px] font-semibold text-success whitespace-nowrap">
                  +{getAmountWithCurrency(refund.amount, currency)}
                </span>
              </div>
            ))
          )}

          <SectionTitle
            title={t('New invoice (penalty)')}
            summary={preview.penaltyInvoice ? getAmountWithCurrency(preview.penaltyInvoice.totalAmount, currency) : undefined}
          />

          {preview.penaltyInvoice ? (
            <>
              {preview.penaltyInvoice.items.map((item) => (
                <EffectRow
                  key={item.description}
                  label={item.description}
                  amount={getAmountWithCurrency(item.amount, currency)}
                  theme="charge"
                />
              ))}
              <div className="text-[11.5px] font-semibold text-warn mt-0.5">
                {t('Due')}: {getClientDateFormat(preview.penaltyInvoice.dueDate)}
              </div>
            </>
          ) : (
            <EffectRow label={t('Will not be issued')} />
          )}
        </>
      )}

      <div className="border-t border-primary pt-2 mt-2.5">
        <span className="text-[11.5px] font-normal text-primaryLight">
          {t('Calculated for {{amount}}', { amount: getClientDateFormat(preview.closeDate) })} · {t('recalculated when options change')}
        </span>
      </div>
    </div>
  );
});
